/** @jsx jsx */
import React, { Dispatch, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { isIPad13, isTablet } from 'react-device-detect';
import FormField from 'components/atoms/Form/FormField';
import FormSubmitArea from 'components/atoms/Form/FormSubmitArea';
import Button, { SecondaryButton, PrimaryButton } from 'components/atoms/Button';
import StaffDomain from 'domain/staff';
import VerticalLabelSelectForm from 'components/molecules/VerticalLabelSelectForm';
import ConfirmActionModal, { ActionType } from 'components/organismos/ConfirmActionModal';
import DataTable from 'components/organismos/DataTable/DataTable';
import { Column } from 'react-table';
import { EmploymentDataStaffDomain } from 'domain/master/general/employmentDataStaff';
import { useEmploymentForm } from './hooks';
import { TablItem } from 'components/atoms/TabBar';
import { css, jsx } from '@emotion/core';
import CalendarForm from 'components/molecules/CalendarForm';
import FlexBox from 'components/atoms/FlexBox';
import useStaffList from 'components/pages/master/general/staff/StaffsPage/hooks';
import { useStaffDelete } from '../StaffListTable/hooks';
import FormLabel from 'components/atoms/Form/FormLabel';
import RadioSelectForm from 'components/molecules/RadioSelectForm';
import FlexBoxItem from 'components/atoms/FlexBoxItem';
import BodyText from 'components/atoms/BodyText';
import FormLayout from 'components/atoms/Form/VerticalLabelFormLayout';
import { OptionType } from 'components/atoms/Select';
import { grayScale, gridCol } from 'components/styles';
import HourInput from 'components/atoms/Form/HourInput';
import SelectForm from 'components/molecules/SelectForm';
import VerticalLabelFormLayout from 'components/atoms/Form/VerticalLabelFormLayout';
import CalendarMonth from 'components/molecules/CalendarV1/CalendarMonth';

const styles = {
  form: css({
    maxWidth: '600px',
    margin: '0 auto',
    padding: '0 15px'
  }),
  centerBox: css({
    justifyContent: 'center',
    marginTop: '30px'
  }),
  calendar: css({
    maxWidth: '200px'
  }),
  span: css({
    margin: '0px 10px',
    position: 'relative',
    top: '-2px'
  }),
  addon: css({
    color: grayScale.gray100,
    padding: '7px',
    marginLeft: '5px'
  }),
  table: css`
    white-space: nowrap;
    overflow-x: auto;
    padding-bottom: 0px;
    table th:nth-of-type(1) {
      width: ${isIPad13 || isTablet ? '85px' : '100px'};
    }
    table th:nth-of-type(2) {
      width: ${isIPad13 || isTablet ? '110px' : '85px'};
    }
    table th:nth-of-type(3) {
      width: ${isIPad13 || isTablet ? '85px' : '85px'};
    }
    table th:nth-of-type(4) {
      width: ${isIPad13 || isTablet ? '85px' : '85px'};
    }
    table th:nth-of-type(5) {
      width: ${isIPad13 || isTablet ? '200px' : '100px'};
    }
    tr td {
      text-align: left;
    }
    tr th {
      text-align: center;
    }
    tr td: first-child {
      text-align: center;
    }
    table tr td:nth-of-type(4) {
      text-align: center;
    }
    table tr td:nth-of-type(5) {
      text-align: center;
    }
  `
}

type SelectType = 'staff' | 'affiliation' | 'employment' | 'laborCost' | 'workPattern' | 'longTermCareandNurse';
const SelectType: { [key in SelectType]: SelectType } = {
  staff: 'staff',
  affiliation: 'affiliation',
  employment: 'employment',
  laborCost: 'laborCost',
  workPattern: 'workPattern',
  longTermCareandNurse: 'longTermCareandNurse'
};

const listHours = [
  '00', '01', '02', '03', '04', '05', '06', '07', '08', '09',
  '10', '11', '12', '13', '14', '15', '16', '17', '18', '19',
  '20', '21', '22', '23',
];
const listTimes = [
  '00', '01', '02', '03', '04', '05', '06', '07', '08', '09',
  '10', '11', '12', '13', '14', '15', '16', '17', '18', '19',
  '20', '21', '22', '23', '24', '25', '26', '27', '28', '29',
  '30', '31', '32', '33', '34', '35', '36', '37', '38', '39',
  '40', '41', '42', '43', '44', '45', '46', '47', '48', '49',
  '50', '51', '52', '53', '54', '55', '56', '57', '58', '59',
];

const EmploymentInfoForm: React.FC<{
  initialStaffDomain?: StaffDomain;
  isEditMode?: boolean;
  tabItems: TablItem[];
  setInitialStaffDomain: Dispatch<React.SetStateAction<StaffDomain>>;
  setSelectType: Dispatch<React.SetStateAction<SelectType>>;
  setTabItems: Dispatch<React.SetStateAction<TablItem[]>>;
  setEmpTargetDate?: React.Dispatch<React.SetStateAction<Date>>;
  employmentOptions: Array<any>;
  isDisabledInfo?: boolean;
  setIsLoading?: Dispatch<React.SetStateAction<boolean>>;
}> = ({
  initialStaffDomain, isEditMode = false, tabItems,
  setInitialStaffDomain, setSelectType, setTabItems,
  setEmpTargetDate,
  employmentOptions,
  isDisabledInfo = false,
  setIsLoading
}) => {
    const {formik, confirmModalOpen, closeConfirmModal} = useEmploymentForm(initialStaffDomain, isEditMode, setIsLoading);

    const [hourFormOptions, setHourFormOptions] = useState<Array<OptionType>>([]);
    const [timeFormOptions, setTimeFormOptions] = useState<Array<OptionType>>([]);

    const dataDeemedWorkingHour = Number(formik.values.deemedWorkingHours) || 0;
    const calcDeemedWorkingH = Math.trunc(dataDeemedWorkingHour ? dataDeemedWorkingHour / 60 : 0);
    const calcDeemedWorkingM =  dataDeemedWorkingHour - (calcDeemedWorkingH * 60);
    const [deemedWorkingHours, setDeemedWorkingHours] = useState<string>(calcDeemedWorkingH ? String(calcDeemedWorkingH < 10 ? '0' + calcDeemedWorkingH : calcDeemedWorkingH) : '00');
    const [deemedWorkingMinutes, setDeemedWorkingMinutes] = useState<string>(calcDeemedWorkingM ? String(calcDeemedWorkingM < 10 ? '0' + calcDeemedWorkingM : calcDeemedWorkingM) : '00');

    useEffect(() => {
      setHourFormOptions(listHours.map((Hours) => ({
        value: Hours,
        label: Hours
      })));
      setTimeFormOptions(listTimes.map((Hours: any) => ({
        value: Hours,
        label: Hours
      })));
    }, []);

    const columns: Array<Column<EmploymentDataStaffDomain>> = React.useMemo(() => [
      {
        Header: '適用開始月',
        accessor: 'dispApplyDate'
      },
      {
        Header: '雇用形態コード',
        accessor: 'employmentCode'
      },
      {
        Header: '雇用形態名',
        accessor: 'employmentName'
      },
      {
        Header: 'みなし時間',
        accessor: 'dispDeemedWorkingHours'
      },
      {
        Header: '時間単位休暇の１日の時間数',
        accessor: 'dispTimeHolidayHourOfDay'
      }
    ], []);

    const { fetchData } = useStaffList();
    const { setDeleteTargetStaff, isOpenDeleteModal, setIsOpenDeleteModal, closeDeleteModal, onSubmitDeleteStaff } = useStaffDelete(fetchData, setIsLoading);

    const convertToMinutes = (hours: number, minutes: number) => {
      return hours * 60 + minutes;
    };

    useEffect(() => {
      const minutes = convertToMinutes(Number(deemedWorkingHours), Number(deemedWorkingMinutes));
      formik.setFieldValue('deemedWorkingHours', minutes);
    }, [deemedWorkingHours, deemedWorkingMinutes]);

    return (
      <React.Fragment>
        {formik.values.employmentDataList
          && formik.values.employmentDataList.length !== 0 && (
            <div className="table-bordered table-padding" css={styles.table}>
              <DataTable
                classNameParrant="border-left-right"
                className="table-no-border-left-right"
                minWidth="100%"
                columns={columns}
                data={formik.values.employmentDataDomainList}
                isHiddenPagination={false}
              />
            </div>
          )}

        <form onSubmit={formik.handleSubmit} css={styles.form}>
          <FormField>
            <div css={styles.calendar}>
              {/* <CalendarForm
                dateFormat={isEditMode ? 'YYYY年MM月' : 'YYYY/MM/DD'}
                label="運用開始日"
                date={formik.values.empApplyDate}
                changeDate={(date: Date) => {
                  if (setEmpTargetDate) {
                    if (!date) {
                      setEmpTargetDate(new Date());
                    } else { setEmpTargetDate(date); }
                  }
                  formik.setFieldValue('empApplyDate', date);
                }}
                required={true}
                readOnly={!isEditMode}
              /> */}
               <VerticalLabelFormLayout
                label={(
                  <FormLabel
                    label="運用開始日"
                    required={true}
                  /> 
                )}
                input={(
                  <CalendarMonth 
                 date={formik.values.empApplyDate}
                 setDate={(date: Date) => {
                  if (setEmpTargetDate) {
                    if (!date) {
                      setEmpTargetDate(new Date());
                    } else { setEmpTargetDate(date); }
                  }
                  formik.setFieldValue('empApplyDate', date);
                }}
                readOnly={!isEditMode}
                />
                )}
                />
            </div>
          </FormField>

          <FormField customStyle={css(isIPad13 || isTablet ? {width: '550px'} : {})} displayBlock={true}>
            <SelectForm
              label="雇用形態"
              name="employmentId"
              value={formik.values.employmentId}
              setValue={(val: string) => { formik.setFieldValue('employmentId', val); }}
              options={employmentOptions}
              note={isEditMode ? '雇用形態を変更すると、勤務パターンが削除され、再登録が必要となります。' : ''}
              errorMsg={formik.errors.employmentId}
              required={true}
              isDisplayBlock={true}
              widthLabel="auto"
            />
          </FormField>
          <FormField displayBlock={true}>
            <RadioSelectForm
              label="みなし時間"
              name="useEmploymentDeemedWorkingHours"
              items={[
                {
                  label: '設定する',
                  value: '0',
                },
                {
                  label: '雇用形態に設定されている日の契約労働時間をみなし勤務時間とする',
                  value: '1',
                },

              ]}
              value={String(formik.values.useEmploymentDeemedWorkingHours ? 1 : 0)}
              setValue={(e) => {
                formik.setFieldValue('useEmploymentDeemedWorkingHours', e.target.value === '0' ? false : 1);
                if( Number(e.target.value) === 1) {
                  formik.setFieldValue('deemedWorkingHours', 0);
                }
              }}
            />
            {
              formik.values.useEmploymentDeemedWorkingHours === false && (
                <FormField displayBlock={true}>
                  <div style={{ marginRight: '12px', width: gridCol.grid06 }}>
                    <FormLayout
                      label={(
                        <FormLabel
                          label=""
                        />
                      )}
                      input={(
                        <FlexBox>
                          <FlexBoxItem width="70px">
                            <VerticalLabelSelectForm
                              label=""
                              name="deemedWorkingHours"
                              value={String(deemedWorkingHours)}
                              setValue={(val: string) => {
                                setDeemedWorkingHours(val);
                              }}
                              options={hourFormOptions}
                            />
                          </FlexBoxItem>
                          <BodyText customStyle={styles.addon}>時間</BodyText>
                          <FlexBoxItem width="70px">
                            <VerticalLabelSelectForm
                              label=""
                              name="deemedWorkingMinutes"
                              value={String(deemedWorkingMinutes)}
                              setValue={(val: string) => {
                                setDeemedWorkingMinutes(val);
                              }}
                              options={timeFormOptions}
                            />
                          </FlexBoxItem>
                          <BodyText customStyle={styles.addon}>分</BodyText>
                        </FlexBox>
                      )}
                    />
                  </div>
                </FormField>
              )
            }
          </FormField>
          <FormField displayBlock={true}>
            <div style={{ marginRight: '12px', width: gridCol.grid06 }}>
              <FormLayout
                label={(
                  <FormLabel
                    label="時間単位休暇の１日の時間数"
                  />
                )}
                input={(
                  <FlexBox>
                    <FlexBoxItem width="70px">
                      <HourInput
                        name="timeHolidayHourOfDay"
                        label=""
                        value={String(formik.values.timeHolidayHourOfDay || 0)}
                        onChange={(val) => {
                          const { value } = val.target;
                          formik.setFieldValue('timeHolidayHourOfDay', Number(value));
                        }}
                        min={0}
                        max={24}
                        isFullW={true}
                      />
                    </FlexBoxItem>
                    <BodyText customStyle={styles.addon}> 時間</BodyText>
                  </FlexBox>
                )}
              />
            </div>
          </FormField>

          <FormSubmitArea customStyle={css({ padding: '0 30px' })}>
            <div style={{ marginRight: '12px' }}>
              <FlexBox customStyle={styles.centerBox}>
                <Button
                  text={isEditMode ? '雇用情報を登録' : '登録'}
                  onClick={formik.handleSubmit}
                  minWidth="150px"
                />
                {
                  isEditMode && !isDisabledInfo ?
                    <div style={{ marginLeft: '12px', textAlign: 'center' }}>
                      <SecondaryButton
                        minWidth="150px"
                        text='削除'
                        onClick={() => {
                          setIsOpenDeleteModal(true);
                          setDeleteTargetStaff(formik.values)
                        }}
                      />
                    </div>
                    : ''
                }

                {!isEditMode && (
                  <div style={{ marginLeft: '12px' }}>
                    <Button
                      text="勤務パターンの入力へ"
                      onClick={() => {
                        if (formik.values.empApplyDate && formik.values.employmentId) {
                          // Set salary apply day
                          formik.values.selectSalary.applyStartDate = formik.values.hireDate;

                          if (tabItems.length <= 3) {
                            setInitialStaffDomain(formik.values);

                            setTabItems([
                              {
                                value: SelectType.staff,
                                display: 'スタッフ情報',
                              },
                              {
                                value: SelectType.affiliation,
                                display: '所属情報',
                              },
                              {
                                value: SelectType.employment,
                                display: '雇用情報',
                              },
                              {
                                value: SelectType.workPattern,
                                display: '勤務パターン',
                              },
                            ]);
                          }
                          setSelectType(SelectType.workPattern);
                        } else {
                          formik.validateForm();
                        }
                      }}
                    />
                  </div>
                )}
              </FlexBox>
            </div>
            <div style={{ textAlign: 'center' }}>
              <Link to="/staffs">
                <PrimaryButton
                  text="スタッフ一覧に戻る"
                  ghost={true}
                  minWidth="150px"
                />
              </Link>
            </div>
          </FormSubmitArea>

        </form>

        {/* 登録内容確認モーダル */}
        <ConfirmActionModal
          open={confirmModalOpen}
          closeHandler={closeConfirmModal}
          onSubmit={formik.handleSubmit}
          actionType={isEditMode ? ActionType.UPDATE : ActionType.CREATE}
        />
        <ConfirmActionModal
          open={isOpenDeleteModal}
          closeHandler={closeDeleteModal}
          onSubmit={onSubmitDeleteStaff}
          actionType={ActionType.DELETE}
          typeSubmit="reject"
        />
      </React.Fragment>
    );
  };

export default EmploymentInfoForm;
